"use client";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import type { ProcessedCourse } from "@/common/course/types";
import Tags from "@/components/Tags";
import { TagProps } from "@/components/Tags/types";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  getCourseStartDate,
  getCourseEndDate,
} from "@/lib/course/utils/courseDates";
import { daysUntilTargetDate } from "@/lib/date";

export type CourseBadge = TagProps & {
  type: "pre-start" | "start-today" | "last-chance" | "custom";
};

interface CourseBadgesProps {
  course: ProcessedCourse;
  badges: CourseBadge[];
  htmlId?: string;
  location?: string;
  name?: string;
  position?: string;
  className?: string;
}

const defaultTextMap = {
  custom: null,
  "pre-start": "Starting Soon",
  "start-today": "Starts Today",
  "last-chance": "Last Chance",
};

const defaultColor = "fern";
const defaultTimingIcon = "calendar-today";

const processTimingBadge = (badge: CourseBadge) => {
  const text = defaultTextMap[badge.type];
  if (!badge.text && !text) {
    return null;
  }
  return {
    ...badge,
    color: badge.color ?? defaultColor,
    icon: badge.icon ?? defaultTimingIcon,
    text: badge.text ?? text,
  };
};

export default function CourseBadges({
  course,
  badges,
  htmlId = "pills-labels",
  location = "hero",
  name = "pills-labels",
  position = "1",
  className = "",
}: CourseBadgesProps) {
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Pills,
      htmlId,
      location,
      name,
      position,
    },
  });
  const { ref, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const startDate = getCourseStartDate(course);
  const endDate = getCourseEndDate(course);
  const startBy =
    typeof startDate === "string" ? new Date(startDate) : startDate;
  const enrollBy = typeof endDate === "string" ? new Date(endDate) : endDate;

  const daysUntilEnrollmentStart = startBy
    ? daysUntilTargetDate(startBy)
    : null;
  const daysUntilEnrollmentEnd = enrollBy
    ? daysUntilTargetDate(enrollBy)
    : null;

  // Starts within 14 days, but not today
  const showPreStartBadge = (badge: CourseBadge) =>
    badge.type === "pre-start" &&
    daysUntilEnrollmentStart !== null &&
    daysUntilEnrollmentStart > 0 &&
    daysUntilEnrollmentStart <= 14;

  // Starts today
  const showStartTodayBadge = (badge: CourseBadge) =>
    badge.type === "start-today" &&
    daysUntilEnrollmentStart !== null &&
    daysUntilEnrollmentStart === 0;

  // Already started but enrollment hasn't ended
  const showLastChanceBadge = (badge: CourseBadge) =>
    badge.type === "last-chance" &&
    daysUntilEnrollmentStart !== null &&
    daysUntilEnrollmentEnd !== null &&
    daysUntilEnrollmentStart < 0 &&
    daysUntilEnrollmentEnd >= 0;

  const processedBadges = badges
    .map((badge) => {
      if (badge.type === "custom") {
        if (!badge.text) {
          return null;
        }
        return {
          ...badge,
          color: badge.color ?? defaultColor,
        };
      }

      if (
        showPreStartBadge(badge) ||
        showStartTodayBadge(badge) ||
        showLastChanceBadge(badge)
      ) {
        return processTimingBadge(badge);
      }

      return null;
    })
    .filter((badge) => badge !== null);

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  if (processedBadges.length === 0) {
    return null;
  }

  return (
    <div ref={ref} className={className}>
      <Tags variant="badges" tags={processedBadges} />
    </div>
  );
}
